/**
 * Where an unauthenticated visitor was headed, carried across the login page.
 *
 * Dana works the AR report in tabs: every client anchor there opens
 * `/clients/{id}?tab=ar` in a new one (`pages/ReportsPage.tsx`). When this was
 * written the session was per-tab, so every one of those tabs arrived
 * unauthenticated, the route guard sent it to /login, and the destination was
 * simply dropped — she signed in and landed on the dashboard, with the client
 * she asked for nowhere in sight.
 *
 * AR-2c overturned that ruling. The session is now shared by every tab
 * (`stores/authStore.ts`), so a new tab usually arrives signed in. The capture
 * still earns its keep on every path that reaches the guard unauthenticated:
 * a browser that has never signed in, and above all an idle expiry
 * (`lib/idleExpiry.ts`), which signs every tab out through the router
 * precisely so this can carry each one back to where it was.
 *
 * The destination travels in router state, never in the URL. That keeps it out
 * of the address bar and out of reach of anyone handing a user a crafted link.
 * It is still sanitised on the way out: router state is persisted into
 * `history.state` and so survives a reload, which makes it a channel we do not
 * wholly control, and an unchecked `navigate()` after login is an open redirect.
 * The check is cheap; the failure it prevents is not.
 */

/** The key the route guards store the destination under, in router state. */
export const REDIRECT_STATE_KEY = 'from'

/** The login route itself — never a legal destination, or login loops. */
const LOGIN_PATH = '/login'

/**
 * Whether a value carries a character a browser strips before resolving a URL.
 *
 * `/<tab>/evil.example` is not a path — the tab is removed first, leaving
 * `//evil.example`, which is another origin. Testing for the raw characters
 * means the string we check is the string the browser will act on.
 *
 * Written as a scan rather than a regex on purpose: a character class over
 * this range has to contain the control characters literally, which puts
 * invisible bytes in the source, trips `no-control-regex`, and makes git call
 * the file binary. The loop says the same thing in the open.
 */
function hasStrippableCharacter(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    if (code <= 0x1f || code === 0x7f) return true
  }
  return false
}

/** The part of a location a destination is built from. */
export interface CapturableLocation {
  pathname: string
  search: string
  hash: string
}

/**
 * The path a guard should remember — pathname, query and fragment, nothing
 * else. Never an origin, so what we store can never name another host.
 */
export function captureRedirectTarget(location: CapturableLocation): string {
  return `${location.pathname}${location.search}${location.hash}`
}

/**
 * The destination if it is safe to honour, otherwise null.
 *
 * Same-origin relative paths only. Anything that could resolve against another
 * host — an absolute URL, a protocol-relative `//host`, its `/\host` backslash
 * twin, a `javascript:` payload — is refused, and the caller falls back to the
 * user's own landing path rather than following it.
 */
export function safeRedirectTarget(value: unknown): string | null {
  if (typeof value !== 'string') return null
  if (value === '') return null

  // Whitespace and control characters are stripped by the browser before the
  // URL is parsed, so test the value only once it cannot change under us.
  if (hasStrippableCharacter(value)) return null

  // A single leading slash is the whole same-origin guarantee. It rejects
  // `http://host`, `javascript:...`, `mailto:...` and bare `host/path` alike.
  if (!value.startsWith('/')) return null

  // ...but two of them is an authority, not a path: `//evil.example` is another
  // origin. So is `/\evil.example` — browsers normalise the backslash to a
  // slash in that position, which is exactly the bypass the single check above
  // would otherwise wave through.
  if (value[1] === '/' || value[1] === '\\') return null

  // The login page is not a destination. Honouring it would bounce a
  // just-authenticated user back to the door they came through.
  if (
    value === LOGIN_PATH ||
    value.startsWith(`${LOGIN_PATH}/`) ||
    value.startsWith(`${LOGIN_PATH}?`) ||
    value.startsWith(`${LOGIN_PATH}#`)
  ) {
    return null
  }

  return value
}

/**
 * The destination a route guard stashed in router state, if any survives the
 * safety check. An explicit logout stores nothing, so this returns null and
 * the caller uses `landingPathFor(user)` — logging out must not pin you to the
 * page you happened to be on.
 */
export function redirectTargetFromState(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null
  return safeRedirectTarget((state as Record<string, unknown>)[REDIRECT_STATE_KEY])
}
